import { Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { AutoCompleteSelectUI, ButtonUI, FormErrorUI, FormLabelUI, InputUI } from '../Interface';
import { generateAutocompleteItems } from '../../utils/helper';

const createEmptyCompanyEmail = (companyId = null) => ({
  companyId,
  documentTypeId: null,
  email: '',
  comment: '',
});

const CompanyEmailTab = ({ disabled, documentTypes = [], selectedValues, valuesState }) => {
  const { errors, setFieldValue, values } = valuesState;
  const rows = values.companyEmail ?? [];
  const documentTypeOptions = generateAutocompleteItems(
    (documentTypes ?? []).filter((docType) => docType.active),
    'name',
    'documentTypeId'
  );

  const handleAddRow = () => {
    setFieldValue('companyEmail', [...rows, createEmptyCompanyEmail(selectedValues?.id ?? null)]);
  };

  const handleDeleteRow = (index) => {
    setFieldValue(
      'companyEmail',
      rows.filter((_row, rowIndex) => rowIndex !== index)
    );
  };

  const handleFieldChange = (index, field) => (event) => {
    const nextRows = rows.map((row, rowIndex) =>
      rowIndex === index
        ? {
            ...row,
            [field]: event.target.value,
          }
        : row
    );

    setFieldValue('companyEmail', nextRows);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Itt adhatók meg az üzleti partnerhez tartozó külön e-mail címzettek.
        </Typography>
        <ButtonUI
          text="Új címzett"
          size="sm"
          className="bg-success"
          onClick={handleAddRow}
          disabled={disabled}
          icon="Add"
          type="button"
        />
      </Box>

      <Box sx={{ display: 'grid', gap: 2 }}>
        {rows.map((row, index) => (
          <Box
            key={row.id ?? `company-email-${index}`}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto',
                },
                alignItems: 'start',
              }}
            >
              <AutoCompleteSelectUI
                id={`companyEmail-documentTypeId-${index}`}
                fullWidth
                label={<FormLabelUI text="Bizonylattípus" />}
                dataset={documentTypeOptions}
                disabled={disabled}
                selectedValue={row.documentTypeId ?? null}
                selectedLabelValue={
                  documentTypeOptions.find((option) => option.value === row.documentTypeId)?.label ?? ''
                }
                helperText={<FormErrorUI message={errors?.companyEmail?.[index]?.documentTypeId} />}
                onChange={(_event, newVal) => {
                  const nextRows = rows.map((currentRow, rowIndex) =>
                    rowIndex === index
                      ? {
                          ...currentRow,
                          documentTypeId: newVal?.value ?? null,
                        }
                      : currentRow
                  );

                  setFieldValue('companyEmail', nextRows);
                }}
              />
              <InputUI
                value={row.email ?? ''}
                onChange={handleFieldChange(index, 'email')}
                fullWidth
                label={<FormLabelUI text="E-mail cím" />}
                variant="standard"
                helperText={<FormErrorUI message={errors?.companyEmail?.[index]?.email} />}
                disabled={disabled}
                InputLabelProps={{ shrink: Boolean(row.email) }}
              />
              <InputUI
                value={row.comment ?? ''}
                onChange={handleFieldChange(index, 'comment')}
                fullWidth
                label={<FormLabelUI text="Megjegyzés" />}
                variant="standard"
                helperText={<FormErrorUI message={errors?.companyEmail?.[index]?.comment} />}
                disabled={disabled}
                InputLabelProps={{ shrink: Boolean(row.comment) }}
              />
              <Box sx={{ pt: { xs: 0, md: 1 } }}>
                <IconButton color="error" onClick={() => handleDeleteRow(index)} disabled={disabled}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}

        {rows.length === 0 && (
          <Box
            sx={{
              p: 3,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Még nincs rögzített e-mail címzett.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CompanyEmailTab;
